export default {
  appName: "Zeus BPM",
  actions: {
    search: "Search",
    clear: "Clear filters",
    retry: "Retry",
    cancel: "Cancel",
    save: "Save",
    submit: "Submit",
    close: "Close",
  },
  /** Toasts for completed actions (`lib/notify.ts`). */
  toast: {
    submitted: "Submitted",
    applicationStarted: "Application started",
    draftCreated: "Draft created",
    decided: "Decision sent",
    bulkApproved: "Approved {{count}} cases",
    saved: "Saved",
    fileCreated: "Created {{name}}",
    secretSaved: "Key saved",
    secretRemoved: "Key deleted",
    modelCreated: 'Created "{{name}}"',
    modelDeleted: 'Deleted "{{name}}"',
    groupDeleted: 'Deleted the group "{{name}}"',
    diagramReplaced: "Diagram replaced",
    formFilesImported: "Imported {{count}} form definitions",
  },
  state: {
    loading: "Loading",
    empty: "No data",
    error: "Failed to load",
  },
  pagination: {
    previous: "Previous",
    next: "Next",
    page: "Page",
    perPage: "Rows per page",
    total: "{{count}} items",
    totalFiltered: "{{count}} (filtered within this page)",
    totalAtLeast: "{{count}} or more",
  },
  myApplications: {
    selfInitiator: "You",
    scopeMine: "Started by me",
    scopeInvolved: "Cases I took part in",
    scopeLabel: "Scope",
    actionRequired: "Action required",
    actionRequiredDescription: "These cases need your input to move forward.",
    showMoreActionRequired: "Show {{count}} more",
    showLessActionRequired: "Show less",
    editDraft: "Edit and submit",
    fixAndResubmit: "Fix and resubmit",
    allApplications: "All cases",
    filters: {
      keyword: "Search by title",
      status: "Status",
      period: "Created",
      allStatuses: "All",
    },
  },
  secrets: {
    title: "Secrets and connections",
    description: "Manage the credentials BPM uses to call external systems.",
    scopeNote:
      "These are only the keys BPM uses to call out. User sign-in is handled by Zeus IdP. Never inline values in a flow: reference them from a Service Task as SPIFF_SECRET:KEY.",
    key: "Key",
    value: "Value",
    owner: "Added by",
    updatedAt: "Updated",
    hidden: "(hidden)",
    add: "Add key",
    update: "Update",
    remove: "Delete",
    empty: "No keys registered",
    valuePrompt: "Enter the value",
    keyPrompt: "Enter the key name",
    authentications: "OAuth connections",
    noAuthentications: "No connections require OAuth",
  },
  formDesign: {
    title: "Form design",
    description: "Review the human task input forms (JSON Schema).",
    files: "Form definitions",
    fields: "Fields",
    preview: "Preview",
    raw: "Schema",
    required: "Required",
    choices: "Choices",
    selectFile: "Select a form to see its contents",
    empty: "No form definitions",
    notForm:
      "This file has no input fields (a UI Schema or other helper file).",
    edit: "Edit",
    cancelEdit: "Cancel editing",
    editorLabel: "Form definition (JSON)",
    editHint:
      "Saving updates the form definition on the server (committing to the repository is a separate step).",
    issueEmpty: "The content is empty",
    issueInvalidJson: "This is not valid JSON",
    issueNotObject: "Write an object ({})",
    issueInvalidProperties: "properties must be an object",
    issueInvalidRequired: "required must be an array",
    issueUnknownRequired:
      'required lists "{{detail}}" which is not in properties (the form would be unfillable)',
  },
  processDesign: {
    newModel: {
      open: "New model",
      title: "Create a new model",
      source: "How to create",
      sourceStarter: "From the starter (one draft task)",
      sourceFile: "From a .bpmn file you have",
      group: "Group",
      groupPlaceholder: "Choose a group",
      groupNew: "+ Create a new group",
      groupId: "Group id",
      groupIdHint:
        "Lowercase, digits and hyphens (becomes the directory name, e.g. hr)",
      groupDisplayName: "Group display name",
      modelName: "Model name",
      modelNameHint:
        "Lowercase, digits and hyphens (becomes the directory name, e.g. interview-request)",
      displayName: "Display name",
      filledFromFileName: "Filled in from the file name",
      filledFromProcessName: "Filled in from the process name in the diagram",
      invalidId: "Use lowercase letters, digits and hyphens.",
      duplicateId: "That id already exists.",
      submit: "Create",
      cancel: "Cancel",
      failed: "The model could not be created.",
      failedDuplicate: "A model with that id already exists.",
      failedGroupDuplicate: 'The group id "{{id}}" already exists.',
      failedFileInvalid: "The BPMN was not accepted: {{detail}}",
      failedForbidden: "You do not have permission to create it.",
      failedGroupMissing: 'The group "{{id}}" does not exist.',
      groupKept:
        'The group "{{name}}" ({{id}}) was created. The group field now points to it.',
      rolledBack:
        "The files (BPMN and draft form) could not be added, so the half-created model was deleted.",
      retry: 'Press "Create" again.',
      leftover:
        "The files (BPMN and draft form) could not be added. A half-created model ({{id}}) is left over. Open it and delete it.",
      groupOption: "{{id}} ({{name}})",
      groupsUnavailable:
        "The group list could not be loaded. Groups without models are not offered.",
      previewFailed:
        "This diagram cannot be displayed (its layout information is missing or broken). You could not view or edit it on this screen, so choose another file.",
      openLeftover: "Open that model",
      note: "Creates a starter with a single draft task. The title field (case_title) is added for you. Edit the rest on this screen.",
      noteFile:
        "Places the chosen .bpmn as is (the file is named after the model). No draft form is created. The title appears in lists once the diagram sets a variable named case_title (for example, a form field named case_title).",
    },
    newForm: {
      open: "Add form",
      fileName: "File name",
      fileNameHint:
        "Lowercase and hyphens. Use *-schema.json for fields, *-uischema.json for layout; a dot also works as the separator (e.g. review-schema.json / review.schema.json).",
      empty: "This model has no form definitions.",
      failed: "Could not create the form definition (no permission).",
      issueEmpty: "Enter a file name.",
      issueInvalid:
        "Contains characters that cannot be used (lowercase, digits and hyphens only).",
      issueConvention:
        "The name must end with *-schema.json or *-uischema.json (.schema.json / .uischema.json also work).",
      issueDuplicate: "A file with the same name already exists.",
    },
    title: "Process design",
    description:
      "Create, review and edit the approval flow definitions (process models).",
    readme: {
      title: "Model description",
      edit: "Edit description",
      create: "Add description",
      save: "Save description",
      cancel: "Cancel",
      label: "Description (Markdown)",
      empty: "This model has no description.",
      saveFailed: "The description could not be saved.",
      conflict: "Someone else updated it. Copy your text, then reload.",
      loadFromFile: "Load from file",
      fileLabel: "Description file",
      fileHint:
        'You can choose one .md file. Its contents go into the editor below (confirm with "Save description").',
      notMarkdown: "Choose a .md file.",
    },
    metadata: {
      title: "Fields shown on cases",
      description:
        "Selected fields appear in the case list and detail (undeclared fields stay hidden even when filled in). Applies to cases started after saving.",
      save: "Save shown fields",
      saveFailed: "Could not save.",
    },
    diagramTitle: "Flow diagram (editable)",
    models: "Process models",
    searchModels: "Search models",
    noMatch: "No models match your search.",
    primaryProcess: "Executable process",
    diagrams: "Diagrams",
    forms: "Form definitions",
    others: "Other files",
    selectModel: "Select a model to see its contents",
    editNote:
      "You can create, edit and delete here. Changes apply to the model on the server. To make them permanent, commit the exported file to process-models/ and deploy with deploy-models.sh (GitOps).",
    edit: "Edit diagram",
    backToView: "Back to view",
    exportHint:
      "Saving updates the model on the server (committing to the repository is a separate step)",
    download: "Export",
    saved: "Saved",
    saveFailed: "Failed to save",
    saveConflict:
      "Someone else has updated this file. Export your version and compare before saving",
    properties: {
      title: "Element settings",
      selectElement: "Select an element to edit its settings",
      name: "Name",
      instructionsForEndUser: "Instructions for the user",
      preScript: "Pre script",
      postScript: "Post script",
      operator: "Operator",
      resultVariable: "Result variable",
      parameters: "Parameters",
      parameterValue: "Value of {{name}}",
      taskProperties: "Task properties",
      messageStart: "Message start",
      messageStartHint:
        "Use the wrench menu on the diagram to change it into a Message Start Event first.",
      messageName: "Message name",
      messageVariable: "Payload variable",
      messageNameRequired:
        "The message name is empty. Start data (such as approvers) will be discarded.",
      messageNameTaken:
        "{{model}} already uses this message name. Saving will fail — pick another name.",
      script: "Script",
      scriptEmpty:
        "The script is empty, so this task does nothing (message start models expand the payload here).",
      formSchema: "Form definition",
      formUiSchema: "UI Schema",
      formNone: "(none)",
      formRequired:
        "No form is set. This task cannot be opened by its assignee.",
      structuredNote:
        "Adding or removing service calls and parameters is done in the SpiffArena UI (:8001). Here you can change existing values.",
    },
    empty: "No process models",
    fileDrop: {
      placeholder: "Click to choose, or drop it here",
      clear: "Remove the chosen file",
      unreadable: "The file could not be read.",
    },
    bpmnFile: {
      label: "BPMN file",
      hint: "You can choose one .bpmn file.",
      preview: "Diagram preview",
      notBpmn: "Choose a .bpmn file.",
      invalidXml: "This is not a readable BPMN file.",
      noExecutableProcess:
        'No executable process found (there is no process, or every process has isExecutable="false").',
      multipleExecutableProcesses:
        "There is more than one executable process ({{ids}}). Leave only one.",
      idSeparator: ", ",
    },
    importDiagram: {
      open: "Import",
      title: "Import a diagram",
      description:
        "Replaces the current diagram ({{file}}) with the contents of the chosen file. The file name stays the same. The old diagram cannot be restored from this screen.",
      preview: "Diagram to import",
      processIdChanged:
        "The executable process changes from {{from}} to {{to}}. The model setting (primary_process_id) is updated as well.",
      processIdSet:
        "The executable process becomes {{to}}. The model setting (primary_process_id) is updated as well.",
      previewFailed:
        "This diagram cannot be displayed (its layout information is missing or broken). Replacing would leave it unviewable and uneditable on this screen, so choose another file.",
      submit: "Replace",
      cancel: "Cancel",
      failed: "The diagram could not be replaced.",
      forbidden: "You do not have permission to replace it.",
      fileInvalid: "The BPMN was not accepted: {{detail}}",
      restoreFailed:
        "The diagram could not be replaced, and the executable process setting may not have been restored. Reload the screen and check the executable process.",
    },
    importForms: {
      open: "Import files",
      title: "Import form definitions",
      description:
        "Places the chosen .json files in this model under their own names. A form definition with the same name is overwritten (the old contents cannot be restored from this screen).",
      fileLabel: "Form definition files",
      fileHint:
        "You can choose several .json files (*-schema.json / *-uischema.json; a dot also works as the separator).",
      selected: "Files to import",
      create: "New",
      overwrite: "Overwrite",
      remove: "Remove {{name}}",
      submit: "Import",
      cancel: "Cancel",
      issueBpmn:
        'Diagrams (.bpmn) are replaced from "Import" next to the diagram.',
      issueNotJson: "Choose a .json file.",
      issueDuplicate: "The same file name was chosen twice.",
      failed: "{{name}} could not be imported.",
      forbidden: "You do not have permission to import {{name}}.",
      fileInvalid: "{{name}} was not accepted: {{detail}}",
      imported: "Imported: {{names}}.",
      remaining: "Not yet imported: {{names}}.",
      nameSeparator: ", ",
    },
    remove: {
      model: "Delete model",
      group: "Delete",
      groupLabel: 'Delete the group "{{name}}"',
      confirmModel:
        'Delete the model "{{name}}" ({{id}})? It cannot be deleted while any case exists.',
      confirmGroup: 'Delete the group "{{name}}" ({{id}})?',
      submit: "Delete",
      cancel: "Cancel",
      hasInstances: "It has cases, so it cannot be deleted.",
      forbidden: "You do not have permission to delete it.",
      notEmpty:
        "This group has models, so it cannot be deleted (the list was reloaded).",
      failed: "Could not delete it.",
    },
  },
  activityLog: {
    title: "Activity log",
    description: "See who did what, and when, across recent cases.",
    scopeNote:
      "Covers the {{count}} most recent cases (fetched per case because there is no cross-case history API). Approvals and rejections are not distinguished in the history — check the case detail for the breakdown.",
    partial:
      "History could not be fetched for {{count}} case(s) (permission or network error).",
    occurredAt: "When",
    actor: "Actor",
    step: "Action",
    filterActor: "Actor",
    filterFrom: "From",
    filterTo: "To",
    empty: "No activity to show",
  },
  taskRun: {
    title: "Fill in the details",
    back: "Back to my cases",
    notAvailable:
      "This form cannot be opened (already submitted, or you lack permission).",
    missingForm:
      "This task has no form attached, so it cannot be opened. Attach one in process design.",
  },
  permissions: {
    title: "My groups and permissions",
    description: "Check the groups you belong to and your BPM permissions.",
    myGroups: "Your groups",
    noGroups: "You do not belong to any group",
    myPermissions: "BPM permissions",
    allowed: "Allowed",
    denied: "Denied",
    sotNote:
      "Group membership is owned by Zeus IdP (OIDC claims) and policy by zeus.yml. Add or remove members in the IdP.",
    targets: {
      caseListMine: "View your own cases",
      caseListAll: "View all cases",
      taskList: "View approval tasks",
      processModelRead: "View case types",
      processModelWrite: "Design processes (create and edit)",
      secretList: "View secrets",
      userSearch: "Search users",
    },
  },
  allCases: {
    title: "All cases",
    description: "Monitor every process instance and spot stuck cases.",
    kpi: {
      inProgress: "In progress",
      stuck: "Stuck (over 5 business days)",
      returned: "Sent back",
      completed: "Completed (recent)",
    },
    elapsed: "Elapsed",
    businessDays: "{{count}} business days",
    empty: "No matching cases",
  },
  notifications: {
    title: "Notifications",
    description: "Approval requests, send-backs and completions.",
    markAllRead: "Mark all as read",
    empty: "No notifications",
    unread: "Unread",
    kinds: {
      approvalRequested: "Approval requested",
      returned: "Sent back",
      completed: "Completed",
    },
    fromApplicant: "{{name}} is asking for your approval",
  },
  home: {
    title: "Home",
    actionNeeded: "Needs your action",
    cards: {
      pendingApprovals: "Waiting for your approval",
      draft: "Drafts",
      returned: "Sent back (action required)",
      completed: "Completed (recent)",
    },
    recent: "Recent cases",
    allClear: "Nothing needs your attention",
  },
  approvals: {
    pending: "Pending",
    history: "Completed",
    requestedAt: "Requested",
    review: "Review",
    back: "Back to approval requests",
    requestContent: "Submitted content",
    openCase: "Open case detail",
    notAvailable:
      "This approval cannot be opened (already handled, or you lack permission).",
    missingForm:
      "This task has no form attached, so it cannot be opened. Attach one in process design.",
    approve: "Approve",
    reject: "Send back",
    rejectReason: "Reason for sending back",
    rejectReasonRequired: "Please enter a reason",
    approved: "Approved",
    rejected: "Sent back",
    empty: "No approvals waiting for you",
    historyEmpty: "No approval history",
    selectAll: "Select all",
    bulkApprove: "Approve selected",
    bulkResult: "Approved {{approved}}",
    bulkSkipped: "{{count}} need individual review",
    bulkFailed: "{{count}} failed",
  },
  savedViews: {
    label: "Views",
    save: "Save this filter",
    namePrompt: "View name",
    delete: "Delete",
    empty: "No saved views",
  },
  newApplication: {
    title: "New case",
    description: "Choose the type of case to start.",
    noRunnable: "No case types are available to you.",
    searchTypes: "Search case types",
    noMatch: "No case types match your search.",
    back: "Back to my cases",
    submit: "Submit",
    saveDraft: "Save draft",
    submitted: "Submitted",
    draftSaved: "Draft saved",
    submitFailed: "Failed to submit",
    noForm: "This case type has no input form.",
    typeDetail: {
      create: "Create with this type",
      back: "Back to new case",
      unknown: "This case type was not found.",
      createFailed: "The case could not be created.",
      createForbidden: "You do not have permission for this operation.",
      createModelInvalid:
        "This model's BPMN has parts that cannot run. Review the diagram in Process design.",
      createErrorElement: "Element (id): {{id}}",
      createdCaseStopped: "Case #{{id}} was created but stopped with an error.",
      createdCaseOpen:
        "Case #{{id}} was created. Open the input form from the case.",
      openCase: "Open the case",
      createRetry:
        "Check My cases to make sure it was not created, then try again.",
      createDetail: "Details",
      createErrorMeta: "Stage: {{stage}} / error_code: {{code}}",
      createErrorStage: "Stage: {{stage}}",
      stage: {
        create: "create",
        run: "run",
        tasks: "fetch tasks",
      },
      hint: "Review the flow before creating. Creating makes a draft case.",
    },
    scopedStart: {
      description:
        "The approver and department are determined from the org structure.",
      unknownModel:
        "This case type cannot be started (no configuration found).",
      noApprovers:
        "No approver was found. Ask an administrator to check the org structure.",
      resolutionFailed:
        "Could not fetch org information. Please try again later.",
      notConfigured:
        "The start endpoint is not configured (ask an administrator).",
      failed: "Failed to start the case.",
    },
  },
  processOverview: {
    title: "Whole process",
    legendCurrent: "Current step",
    legendCompleted: "Done",
    legendError: "Error",
    unavailable: "The process diagram could not be displayed.",
  },
  backTo: "Back to {{name}}",
  backTargets: {
    approvalReview: "this approval",
  },
  caseDetail: {
    decidedBy: "Decided by",
    forbidden:
      "You do not have permission to open this case. Ask an administrator for access.",
    title: "Case detail",
    back: "Back to my cases",
    notFound: "This case cannot be opened (invalid URL).",
    diagramUnavailable: "The diagram could not be displayed.",
    sections: {
      summary: "Summary",
      process: "About this process",
      history: "History",
    },
    caseContent: "This case",
    /** Inline actions on the detail page (shown only when it is your turn). */
    action: {
      next: "Next approval ({{name}})",
      title: "What to do",
      review: "Approve",
      draft: "Fix and resubmit",
      reviewNote: "This case is waiting for your decision.",
      draftNote: "You can edit the content and submit it again.",
      formMissing:
        "This task has no form, so it cannot be handled here. Attach a form in process design.",
      submitFailed: "Could not submit. Please try again later.",
    },
    returnedReason: "Reason for return",
    returnedReasonEmpty: "No reason was given.",
    processDescriptionNote:
      "This description is shared by all cases of this type.",
    initiator: "Submitted by",
    startedAt: "Started at",
    endedAt: "Completed at",
    businessData: "Submitted data",
    noHistory: "No history",
    diagram: "Diagram",
    saveDiagram: "Save",
    saveFailed: "Failed to save",
    saved: "Saved",
    eventTypes: {
      task_completed: "Completed",
      task_cancelled: "Cancelled",
      process_instance_rewound_to_task: "Rewound",
      task_data_edited: "Data edited",
      task_executed_manually: "Run manually",
      task_skipped: "Skipped",
      process_instance_error: "Error",
      process_instance_force_run: "Force run",
      process_instance_migrated: "Migrated",
      process_instance_resumed: "Resumed",
      process_instance_suspended: "Suspended",
      process_instance_terminated: "Terminated",
    },
  },
  placeholderPage: {
    title: "Coming soon",
    description: "This screen is not implemented yet.",
  },
  notFound: {
    title: "Page not found",
    description: "Please check the URL.",
    backToHome: "Back to home",
  },
} as const;
